import { useMutation } from "@tanstack/react-query";
import { Loader2, OctagonX } from "lucide-react";
import { useId, useState } from "react";
import {
  CANCEL_RECEIVED_ORDER_NOTICE,
  type OrderCancelInput,
  type OrderCancelReason,
  type WriteRejection,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { RejectionNotice } from "@/modules/orders/screens/RejectionNotice";
import { CANCEL_REASONS as REASONS } from "@/modules/orders/cancel";
import { useOrderWrite } from "@/modules/orders/useOrderWrite";

/** Shopify's limit on a cancellation's staff note. */
const STAFF_NOTE_MAX = 255;

interface FormState extends Omit<OrderCancelInput, "reason" | "staffNote"> {
  reason: OrderCancelReason | null;
  staffNote: string;
}

/**
 * Restock on; refund and customer email off. Stock that is not put back is
 * stock both the store and the ERP believe is gone, while a refund and an
 * email cannot be taken back once sent — each can still be done afterwards
 * in Shopify admin if it was wanted.
 */
const DEFAULTS: FormState = {
  reason: null,
  refund: false,
  restock: true,
  notifyCustomer: false,
  staffNote: "",
};

function Option({
  checked,
  onChange,
  disabled,
  title,
  hint,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled: boolean;
  title: string;
  hint: string;
}) {
  const id = useId();
  return (
    <div className="flex items-start gap-2.5">
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(value) => {
          onChange(value === true);
        }}
        className="mt-0.5"
      />
      <div className="space-y-0.5">
        <Label htmlFor={id} className="text-sm font-medium">
          {title}
        </Label>
        <p className="text-muted-foreground text-xs">{hint}</p>
      </div>
    </div>
  );
}

/**
 * Cancel this order in Shopify (#169), behind a dialog that names it.
 *
 * Shopify's own choices — reason, refund, restock, notify the customer, a
 * staff note — with safe defaults. `orderCancel` runs as a job; the API waits
 * for it and writes the order back from what Shopify then says, so the
 * "Cancelled" badge appears only once Shopify has actually cancelled it. A
 * refusal keeps the dialog open with every choice as it was.
 *
 * The guard is the `channelUpdatedAt` seen when the dialog opened, not at
 * confirm time — a background refetch must not swap in a newer one and let
 * the cancel sail over a change made in Shopify admin meanwhile (#166).
 */
export function OrderCancelAction({
  orderId,
  orderNumber,
  channelUpdatedAt,
  cancelledAt,
  rtoReceived = false,
}: {
  orderId: string;
  orderNumber: string;
  channelUpdatedAt: string;
  cancelledAt: string | null;
  /**
   * The ERP has received this order's returned parcel and put the stock back
   * (#529). Restock is then off and cannot be ticked, and the dialog says why:
   * cancelling with it on would add the stock a second time.
   */
  rtoReceived?: boolean;
}) {
  const data = useDataSource();
  const write = useOrderWrite(orderId);
  const reasonId = useId();
  const noteId = useId();

  const [open, setOpen] = useState(false);
  const [guard, setGuard] = useState(channelUpdatedAt);
  const [form, setForm] = useState<FormState>(DEFAULTS);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  const mutation = useMutation({
    mutationFn: (input: OrderCancelInput) =>
      data.orders.cancel(orderId, input, { expectedUpdatedAt: guard }),
    onSuccess: (result) => {
      if (result.ok) {
        setOpen(false);
        setRejection(null);
        write.accepted(result.data);
      } else {
        setRejection(result.rejection);
      }
    },
  });

  // Already cancelled: the header's badge says so, and there is nothing to do.
  if (cancelledAt) return null;

  const busy = mutation.isPending;
  const noteTooLong = form.staffNote.trim().length > STAFF_NOTE_MAX;

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="text-destructive hover:text-destructive"
        onClick={() => {
          setGuard(channelUpdatedAt);
          setForm({ ...DEFAULTS, restock: !rtoReceived });
          setRejection(null);
          setOpen(true);
        }}
      >
        <OctagonX />
        Cancel order
      </Button>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!busy) setOpen(next);
        }}
      >
        <DialogContent
          title={`Cancel order ${orderNumber}?`}
          description="This cannot be undone."
        >
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm">
              The cancellation is sent to Shopify. This order shows as cancelled
              once Shopify confirms it.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor={reasonId} className="text-sm font-medium">
                Reason
              </Label>
              <Select
                value={form.reason ?? undefined}
                disabled={busy}
                onValueChange={(value) => {
                  setForm({ ...form, reason: value as OrderCancelReason });
                }}
              >
                <SelectTrigger id={reasonId} className="w-full">
                  <SelectValue placeholder="Choose a reason" />
                </SelectTrigger>
                <SelectContent>
                  {REASONS.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Option
                checked={form.restock && !rtoReceived}
                disabled={busy || rtoReceived}
                onChange={(restock) => {
                  setForm({ ...form, restock });
                }}
                title="Restock items"
                hint={
                  rtoReceived
                    ? CANCEL_RECEIVED_ORDER_NOTICE
                    : "Put the order's items back into available stock."
                }
              />
              <Option
                checked={form.refund}
                disabled={busy}
                onChange={(refund) => {
                  setForm({ ...form, refund });
                }}
                title="Refund payment"
                hint="Refund what was paid to the original payment method. A payment that was only authorised is voided either way."
              />
              <Option
                checked={form.notifyCustomer}
                disabled={busy}
                onChange={(notifyCustomer) => {
                  setForm({ ...form, notifyCustomer });
                }}
                title="Notify customer"
                hint="Shopify emails the customer that the order was cancelled."
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor={noteId} className="text-sm font-medium">
                Staff note{" "}
                <span className="text-muted-foreground font-normal">
                  (optional, staff only)
                </span>
              </Label>
              <Textarea
                id={noteId}
                rows={2}
                value={form.staffNote}
                disabled={busy}
                aria-invalid={noteTooLong}
                onChange={(event) => {
                  setForm({ ...form, staffNote: event.target.value });
                }}
              />
              {noteTooLong ? (
                <p className="text-destructive text-xs">
                  At most {STAFF_NOTE_MAX} characters.
                </p>
              ) : null}
            </div>

            {rejection ? (
              <RejectionNotice
                rejection={rejection}
                onReload={() => {
                  void write.reload();
                  setOpen(false);
                  setRejection(null);
                }}
              />
            ) : null}

            <div className="flex flex-col-reverse gap-2 border-t pt-3 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                disabled={busy}
                onClick={() => {
                  setOpen(false);
                }}
              >
                Keep order
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={form.reason === null || noteTooLong || busy}
                onClick={() => {
                  if (form.reason === null) return;
                  setRejection(null);
                  mutation.mutate({
                    reason: form.reason,
                    refund: form.refund,
                    // Never on for a received order: its stock is already back.
                    restock: form.restock && !rtoReceived,
                    notifyCustomer: form.notifyCustomer,
                    staffNote: form.staffNote.trim() || null,
                  });
                }}
              >
                {busy ? <Loader2 className="animate-spin" /> : null}
                Cancel order {orderNumber}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
